import React, { useEffect, useState } from 'react';
import { Box, Drawer, SwipeableDrawer, IconButton, Typography, InputBase, Chip, Divider, Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import BlockIcon from '@mui/icons-material/Block';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SpeedIcon from '@mui/icons-material/Speed';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LabelOutlineIcon from '@mui/icons-material/LabelOutlined';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PaymentsIcon from '@mui/icons-material/Payments';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ContactsIcon from '@mui/icons-material/Contacts';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DownloadIcon from '@mui/icons-material/Download';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CategoryIcon from '@mui/icons-material/Category';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CampaignIcon from '@mui/icons-material/Campaign';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import ShieldIcon from '@mui/icons-material/Shield';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useNavigate } from 'react-router-dom';
import { radius, layout, elevation } from '../../themes/smartinventoryTheme';
import useDesignTokens from '../ui/useDesignTokens';
import StatusChip from '../ui/StatusChip';
import useHelpPanel from './useHelpPanel';
import SupportForm from './SupportForm';
import eventBus from '../../utils/eventBus';

const TOPIC_ICONS = {
  Speed: SpeedIcon,
  AddCircleOutline: AddCircleOutlineIcon,
  EditNote: EditNoteIcon,
  LabelOutline: LabelOutlineIcon,
  MarkEmailRead: MarkEmailReadIcon,
  Payments: PaymentsIcon,
  PictureAsPdf: PictureAsPdfIcon,
  Contacts: ContactsIcon,
  WarningAmberOutlined: WarningAmberOutlinedIcon,
  TrendingUp: TrendingUpIcon,
  AccountBalanceWallet: AccountBalanceWalletIcon,
  VisibilityOff: VisibilityOffIcon,
  PhotoLibrary: PhotoLibraryIcon,
  Download: DownloadIcon,
  SyncAlt: SyncAltIcon,
  Category: CategoryIcon,
  Assessment: AssessmentIcon,
  LocalOffer: LocalOfferIcon,
  PeopleOutline: PeopleOutlineIcon,
  EventBusy: EventBusyIcon,
  Campaign: CampaignIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  PersonAdd: PersonAddIcon,
  VerifiedUser: VerifiedUserIcon,
  SwapHoriz: SwapHorizIcon,
  HourglassBottom: HourglassBottomIcon,
  HandshakeOutlined: HandshakeOutlinedIcon,
  Shield: ShieldIcon,
  Inventory2: Inventory2Icon,
};

const RULE_ICONS = { ReceiptLong: ReceiptLongIcon, EventRepeat: EventRepeatIcon, Block: BlockIcon, ArrowUpward: ArrowUpwardIcon };

const TopicRow = ({ topic, onOpen }) => {
  const Icon = TOPIC_ICONS[topic.icon] || HelpOutlineIcon;
  return (
    <Box
      role="button"
      tabIndex={0}
      data-testid={`help-topic-${topic.id}`}
      onClick={() => onOpen(topic)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(topic); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 1, cursor: 'pointer',
        borderRadius: `${radius.sm}px`, '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Icon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" noWrap>{topic.title}</Typography>
        {topic.subtitle && <Typography variant="caption" color="text.secondary" component="div" noWrap>{topic.subtitle}</Typography>}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 18, color: 'divider', flexShrink: 0 }} />
    </Box>
  );
};

const TourCard = ({ tourMeta, pageLabel, t, onStart, isNarrow }) => {
  const { tokens } = useDesignTokens();
  return (
    <Box
      role="button"
      tabIndex={isNarrow ? -1 : 0}
      onClick={isNarrow ? undefined : onStart}
      onKeyDown={isNarrow ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') onStart(); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1.5,
        border: `1px solid ${tokens.accentBorder}`, bgcolor: tokens.accentTintSoft,
        borderRadius: `${radius.lg}px`, cursor: isNarrow ? 'default' : 'pointer',
      }}
    >
      <Box sx={{
        width: 34, height: 34, borderRadius: `${radius.sm}px`, bgcolor: 'primary.main',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
      >
        <PlayArrowIcon sx={{ color: 'primary.contrastText', fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" noWrap>{t('panel.showMeAround', { page: pageLabel })}</Typography>
        <Typography variant="caption" color="text.secondary">
          {isNarrow ? t('tour.readTheGuide', { defaultValue: 'Read the guide' }) : t('panel.tourStops', { count: tourMeta.stopCount })}
        </Typography>
      </Box>
    </Box>
  );
};

const RuleRow = ({ icon, title, text }) => {
  const Icon = RULE_ICONS[icon] || HelpOutlineIcon;
  return (
    <Box sx={{ display: 'flex', gap: 1.25, py: 1 }}>
      <Icon sx={{ fontSize: 17, color: 'primary.main', flexShrink: 0, mt: 0.25 }} />
      <Box>
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" color="text.secondary">{text}</Typography>
      </Box>
    </Box>
  );
};

const PlanLimitArticle = ({ content, t, isAdmin }) => {
  const navigate = useNavigate();
  if (!content) {
    return <Typography variant="body2" color="text.secondary">{t('panel.noTopicsYet')}</Typography>;
  }
  const tone = content.used >= (content.limit ?? Infinity) ? 'error' : (content.limit != null && content.used / content.limit >= 0.8 ? 'warning' : 'success');
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>{content.title}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>{content.meta}</Typography>
      <Box sx={{ bgcolor: 'action.hover', borderRadius: `${radius.md}px`, p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{content.meterCaption}</Typography>
          {content.usedOfLimitText && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: `${tone}.main` }}>{content.usedOfLimitText}</Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">{content.renewsCaption}</Typography>
      </Box>
      <RuleRow icon="ReceiptLong" title={content.whatCountsTitle} text={content.whatCounts} />
      <Divider sx={{ my: 0.5 }} />
      {content.whenResetsTitle && (
        <>
          <RuleRow icon="EventRepeat" title={content.whenResetsTitle} text={content.whenResets} />
          <Divider sx={{ my: 0.5 }} />
        </>
      )}
      <RuleRow icon="Block" title={content.atLimitTitle} text={content.atLimitText} />
      <Divider sx={{ my: 0.5 }} />
      <RuleRow icon="ArrowUpward" title={content.needMoreTitle} text={content.needMore} />
      <Button
        fullWidth
        variant="contained"
        disabled={!isAdmin}
        onClick={() => navigate('/subscription')}
        sx={{ mt: 2 }}
      >
        {content.comparePlans}
      </Button>
      {!isAdmin && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, textAlign: 'center' }}>
          {content.adminOnlyNote}
        </Typography>
      )}
    </Box>
  );
};

const StaticArticle = ({ article, onOpenArticle }) => {
  const { tokens } = useDesignTokens();
  return (
  <Box>
    <Typography variant="h6" sx={{ mb: 0.5 }}>{article.title}</Typography>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
      {article.minutes} min read · updated {article.updated}
    </Typography>

    {article.statuses && article.statuses.map((row) => (
      <Box key={row.status} sx={{ display: 'flex', gap: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <StatusChip status={row.status} label={row.label} />
        <Typography variant="body2" color="text.secondary">{row.text}</Typography>
      </Box>
    ))}

    {article.body && <Typography variant="body2" color="text.secondary">{article.body}</Typography>}

    {article.callout && (
      <Box sx={{ mt: 1.5, p: 1.25, borderRadius: `${radius.md}px`, bgcolor: tokens.warningTint, border: `1px solid ${tokens.warningBorder}` }}>
        <Typography variant="body2">
          <strong>{article.callout.title}</strong> {article.callout.text}
        </Typography>
      </Box>
    )}

    {article.related && article.related.length > 0 && (
      <Box sx={{ mt: 2 }}>
        {article.related.map((rel) => (
          <Typography
            key={rel.articleId}
            variant="subtitle1"
            color="primary"
            sx={{ cursor: 'pointer', mb: 0.75, '&:hover': { textDecoration: 'underline' } }}
            onClick={() => onOpenArticle(rel.articleId)}
          >
            {rel.label}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
  );
};

const ArticleFeedback = ({ articleId, t }) => {
  const [vote, setVote] = useState(null);
  const cast = (v) => {
    setVote(v);
    eventBus.emit(eventBus.EVENTS.HELP_FEEDBACK, { articleId, vote: v });
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary">
        {vote ? t('panel.thanksForFeedback') : t('panel.wasThisHelpful')}
      </Typography>
      {!vote && (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" aria-label={t('panel.wasThisHelpful')} onClick={() => cast('up')}>
            <ThumbUpOffAltIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label={t('panel.wasThisHelpful')} onClick={() => cast('down')}>
            <ThumbDownOffAltIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const PanelContent = ({ panel, isAdmin }) => {
  const {
    t, help, pageTopics, pageLabel, popular, activeArticle, activeFeatureKey,
    planLimitContent, results, resultsOnThisPage, openTopic, openResult, lastArticlePreview, tourMeta,
  } = panel;
  const { tokens } = useDesignTokens();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [supportSent, setSupportSent] = useState(false);
  useEffect(() => { if (help.view !== 'support') setSupportSent(false); }, [help.view]);
  const isDetailView = help.view === 'article' || help.view === 'support';
  const currentBreadcrumb = activeFeatureKey ? planLimitContent?.breadcrumb : activeArticle?.breadcrumb;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="help-panel">
      {isDetailView ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '18px 20px 12px' }}>
          <IconButton size="small" aria-label={t('panel.back')} onClick={help.goBack}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
            {help.view === 'support' ? t('panel.messageSupport') : `${pageLabel ? `${pageLabel} › ` : ''}${currentBreadcrumb || ''}`}
          </Typography>
          <IconButton size="small" aria-label={t('panel.close')} onClick={help.close}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '18px 20px 12px' }}>
            <HelpOutlineIcon sx={{ fontSize: 19, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ flex: 1 }}>{t('panel.title')}</Typography>
            <Tooltip title={t('panel.openFullGuide')}>
              <IconButton size="small" aria-label={t('panel.openFullGuide')} onClick={() => help.openSearch('')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" aria-label={t('panel.closeHelp')} onClick={help.close}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, height: 38, px: 1.5,
                borderRadius: `${radius.pill}px`, border: '1px solid', borderColor: help.view === 'search' ? 'primary.main' : 'divider',
                borderWidth: help.view === 'search' ? 1.5 : 1,
              }}
            >
              <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <InputBase
                fullWidth
                placeholder={t('panel.searchPlaceholder')}
                value={help.searchQuery}
                onChange={(e) => help.openSearch(e.target.value)}
                onFocus={() => { if (help.view !== 'search') help.openSearch(help.searchQuery); }}
                inputProps={{ 'data-testid': 'help-search-input' }}
                sx={{ fontSize: 13.5 }}
              />
              {help.view === 'search' && help.searchQuery ? (
                <IconButton size="small" aria-label={t('panel.clearSearch')} onClick={() => { help.setSearchQuery(''); help.showTopics(); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : (
                <Chip label="?" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {help.view === 'topics' && (
          <>
            {lastArticlePreview && (
              <Box
                role="button"
                tabIndex={0}
                onClick={() => help.open(lastArticlePreview.articleId)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, p: 1, borderRadius: `${radius.sm}px`, cursor: 'pointer', bgcolor: 'action.hover' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" component="div">{t('panel.continueReading')}</Typography>
                  <Typography variant="subtitle1" noWrap>{lastArticlePreview.title}</Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 18, color: 'divider' }} />
              </Box>
            )}
            <Typography variant="overline" component="div" sx={{ color: 'text.disabled', mb: 1 }}>{t('panel.onThisPage')}</Typography>
            {pageLabel && (
              <Chip
                label={pageLabel}
                size="small"
                sx={{ mb: 1.5, bgcolor: tokens.accentTintStrong, color: tokens.accentInk }}
              />
            )}
            {tourMeta && (
              <TourCard
                tourMeta={tourMeta}
                pageLabel={pageLabel}
                t={t}
                isNarrow={isNarrow}
                onStart={() => help.startTour(help.route)}
              />
            )}
            {pageTopics.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('panel.noTopicsYet')}</Typography>
            )}
            {pageTopics.map((topic) => (
              <TopicRow key={topic.id} topic={topic} onOpen={openTopic} />
            ))}

            <Typography variant="overline" component="div" sx={{ color: 'text.disabled', mt: 2.5, mb: 1 }}>{t('panel.popularEverywhere')}</Typography>
            {popular.map((item) => (
              <Typography
                key={item.id}
                variant="subtitle1"
                color="primary"
                sx={{ cursor: 'pointer', mb: 1, '&:hover': { textDecoration: 'underline' } }}
                onClick={() => help.openArticle(item.articleId, 'topics')}
              >
                {item.title}
              </Typography>
            ))}
          </>
        )}

        {help.view === 'search' && (
          <>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
              {help.searchQuery
                ? `${t('panel.resultsCount', { count: results.length })} · ${t('panel.resultsOnThisPage', { count: resultsOnThisPage })}`
                : null}
            </Typography>
            {help.searchQuery && results.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('panel.noResultsFor', { query: help.searchQuery })}</Typography>
            )}
            {results.map((r) => (
              <Box
                key={`${r.route}-${r.articleId}`}
                role="button"
                tabIndex={0}
                onClick={() => openResult(r)}
                sx={{ py: 1, px: 1, cursor: 'pointer', borderRadius: `${radius.sm}px`, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Chip
                    size="small"
                    label={r.pageLabel}
                    sx={r.route === help.route
                      ? { bgcolor: tokens.accentTintStrong, color: tokens.accentInk }
                      : { bgcolor: tokens.neutralTint, color: tokens.inkSoft }}
                  />
                  <Typography variant="subtitle1" noWrap>{r.title}</Typography>
                </Box>
                {r.snippet && <Typography variant="caption" color="text.secondary" component="div" noWrap>{r.snippet}</Typography>}
              </Box>
            ))}
          </>
        )}

        {help.view === 'article' && (
          activeFeatureKey ? (
            <PlanLimitArticle content={planLimitContent} t={t} isAdmin={isAdmin} />
          ) : activeArticle ? (
            <>
              <StaticArticle article={activeArticle} onOpenArticle={(id) => help.openArticle(id, 'article')} />
              <ArticleFeedback articleId={help.activeArticleId} t={t} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">{t('panel.noTopicsYet')}</Typography>
          )
        )}

        {help.view === 'support' && (
          supportSent ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('support.sentTitle', { defaultValue: 'Message sent' })}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('panel.footerReply')}</Typography>
              <Button variant="outlined" onClick={help.showTopics}>{t('panel.back', { defaultValue: 'Back' })}</Button>
            </Box>
          ) : (
            <SupportForm
              route={help.route}
              pageLabel={pageLabel}
              onSent={() => setSupportSent(true)}
              onCancel={help.goBack}
            />
          )
        )}
      </Box>

      {(help.view === 'topics') && (
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>{t('panel.didntFindIt')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<ChatBubbleOutlineIcon fontSize="small" />} onClick={help.openSupport} sx={{ flex: 1 }}>
              {t('panel.messageSupport')}
            </Button>
            <Button size="small" variant="outlined" startIcon={<MenuBookIcon fontSize="small" />} onClick={() => help.openSearch('')} sx={{ flex: 1 }}>
              {t('panel.fullGuide')}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, textAlign: 'center' }}>
            {t('panel.footerReply')}
          </Typography>
        </Box>
      )}

      {help.view === 'search' && help.searchQuery && results.length === 0 && (
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>{t('panel.notWhatYouMeant')}</Typography>
          <Button size="small" variant="outlined" onClick={help.openSupport} fullWidth>
            {t('panel.askSupportAbout', { query: help.searchQuery })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// A native form field eating "?" (typing a literal question mark) or Escape
// (clearing/cancelling an edit) must win over the global shortcuts below.
const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
};

// Mounted once (see AppShell.js) for the admin and GA shells only — the
// customer shell never sets `enableHelp`, so this component is simply never
// rendered there. Persistent right Drawer (380px, shifts page content —
// AppShell handles the margin) on desktop/tablet; bottom SwipeableDrawer at
// 80vh below the `sm` breakpoint, per design_handoff_help_system/README.md.
const HelpPanel = () => {
  const panel = useHelpPanel();
  const { help, isMobile } = panel;
  const isAdmin = localStorage.getItem('userRole') === 'admin';

  // "?" opens search from anywhere on the page; Esc closes the panel. The
  // mobile SwipeableDrawer and HelpMark's Popover already close on Escape on
  // their own (both Modal-based) — this only needs to cover the desktop
  // persistent Drawer, which isn't a Modal and so doesn't get that for free.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (isTypingTarget(document.activeElement)) return;
      if (e.key === '?') {
        e.preventDefault();
        help.openSearch(help.searchQuery);
      } else if (e.key === 'Escape' && help.isOpen && !isMobile) {
        help.close();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [help.isOpen, isMobile]);

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={help.isOpen}
        onOpen={() => {}}
        onClose={help.close}
        disableSwipeToOpen
        PaperProps={{ sx: { height: '80vh', borderRadius: `${radius.xl}px ${radius.xl}px 0 0`, boxShadow: elevation.overlay } }}
      >
        <PanelContent panel={panel} isAdmin={isAdmin} />
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={help.isOpen}
      PaperProps={{
        sx: {
          width: layout.helpPanelWidth, border: 'none', borderLeft: '1px solid',
          borderColor: 'divider', boxShadow: elevation.helpPanel,
        },
      }}
    >
      <PanelContent panel={panel} isAdmin={isAdmin} />
    </Drawer>
  );
};

export default HelpPanel;
export { PanelContent };
